/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import * as emotion from "emotion";
import {BaseComponent, Dialog_Builder, DialogModule, ToastModule, TS_Checkbox, TS_Input} from "@intuitionrobotics/thunderstorm/frontend";
import {Loader} from "../../../../widgets/Loader";
import {DB_Object, generateHex, PartialProperties} from "@intuitionrobotics/ts-common";
import {inputTextField} from "@styles/styles";
import {DialogCard} from "../../widget/DialogCard";
import {DB_User} from "@app/ir-q-app-common/types/care-center/user";
import {OnUsersUpdated, UsersModule} from "@modules/UsersModule";
import {genders} from "@app/app-shared/patient";

type State = {
    loading: boolean
    samlAccount: boolean,
    password?: string,
    user: PartialProperties<DB_User, keyof DB_Object>
}
type Props = {
    existingUser?: DB_User;
}

const content = emotion.css(
    {
        boxSizing: "border-box"
    }
);

const body = emotion.css(
    {
        padding: 20,
        width: "100%",
        boxSizing: "border-box"
    }
);

const inputText = emotion.css(
    {
        marginBottom: 15
    }
);

const inputTextLabel = emotion.css(
    {
        marginBottom: 5
    }
);

export class AddUserDialog
    extends BaseComponent<Props, State>
    implements OnUsersUpdated {

    __onUsersUpdated() {
        DialogModule.close();
    }

    public static show(existingUser?: DB_User) {
        new Dialog_Builder(
            <AddUserDialog
                existingUser={existingUser}
            />
        ).setAllowIndirectClosing(true)
            .show();
    }

    constructor(props: Props) {
        super(props);

        const existingUser = this.props.existingUser;

        this.state = {
            loading: false,
            samlAccount: false,
            user: {
                first: "",
                last: "",
                mail: "",
                title: "",
                mailEnabled: false,
                ...existingUser
            }
        };
    }

    private submit = () => {
        const first = this.state.user.first.trim();
        const last = this.state.user.last.trim();
        const mail = this.state.user.mail.trim();

        if (!first || !last || !mail) {
            this.setState({loading: false});
            return;
        }

        if (!this.state.samlAccount && (!this.state.password || this.state.password.length < 8)) {
            ToastModule.toastError("Password needs to be at least 8 characters")
            this.setState({loading: false});
            return;
        }


        UsersModule.createWithKasperAccount(this.state.user, this.state.password, this.state.password);
    };

    render() {
        const user = this.state.user;
        return !user ?
            <Loader/> :
            <DialogCard
                showX={true}
                loading={this.state.loading}
                content={
                    this.renderAddOrganizationContent()
                }
                saveLabel={`${this.props.existingUser ? "Update" : "Add"} user`}
                cancelLabel={"Cancel"}
                title={`${this.props.existingUser ? "Update" : "Add"} user`}
                onSave={() => {
                    this.setState({loading: true}, () => this.submit());
                    // Keep dialog open.
                    return true;
                }}
                saveEnabled={!!(
                    user.first.trim() &&
                    user.last.trim() &&
                    user.mail.trim() &&
                    (this.state.samlAccount || this.state.password?.trim())
                )}
            />;
    }

    private renderAddOrganizationContent = () => {
        return <div className={`ll_v_l fill ${content}`}>
            {this.renderContent()}
        </div>;
    };

    private renderContent = () => {
        return <div className={`${body} ll_v_c`}>
            <div className={`ll_v_c`}>
                {this.renderIsSamlAccount()}
                {this.renderFirstNameField()}
                {this.renderLastNameField()}
                {this.renderMailField()}
                {this.renderDailyEmails()}
                {this.renderPasswordField()}
                {this.renderTitleField()}
                {this.renderGenderField()}
            </div>
        </div>;
    };

    private renderIsSamlAccount = () => {
        return <div className={`${inputText} ll_v_l`}>
            <TS_Checkbox
                key={"samlAccount"}
                value={this.state.samlAccount}
                checked={this.state.samlAccount}
                label={"Saml account"}
                onCheck={(value) => {
                    this.setState({samlAccount: !value, password: undefined})
                }}/>
        </div>;
    };

    private renderGenderField = () => {
        return <div className={`${inputText} ll_v_l`}>
            <div className={inputTextLabel}>
                Gender:
            </div>
            <div>
                {genders.map(gender => {
                    return <TS_Checkbox
                        key={gender.key}
                        value={gender}
                        checked={gender.key === this.state.user.gender}
                        label={gender.label}
                        onCheck={(value) => this.setState(state => ({
                            user: {
                                ...state.user,
                                gender: value.key
                            }
                        }))}/>;
                })}
            </div>
        </div>;
    };

    private renderDailyEmails = () => {
        return <div className={`${inputText} ll_v_l`}>
            <div className={inputTextLabel}>
            </div>
            <div>
                <TS_Checkbox
                    key={"dailyEmails"}
                    value={this.state.user.mailEnabled}
                    checked={!!this.state.user.mailEnabled}
                    label={"Subscribe to daily notification emails"}
                    onCheck={(value) => this.setState(state => ({
                        user: {
                            ...state.user,
                            mailEnabled: !value
                        }
                    }))}/>
            </div>
        </div>;
    };


    private renderPasswordField = () => {
        if (this.state.samlAccount)
            return null;

        return <div className={`${inputText} ll_v_l`}>
            <div className={inputTextLabel}>
                Password:
            </div>
            <div>
                <TS_Input<string>
                    className={inputTextField}
                    id={"user-password"}
                    value={this.state.password}
                    placeholder={"Password"}
                    onChange={(value: string) => this.setState(state => ({password: value}))}
                    type={"text"}
                    name={generateHex(8)}
                    spellCheck={true}/>
            </div>
        </div>;
    };

    private renderTitleField = () => {
        return <div className={`${inputText} ll_v_l`}>
            <div className={inputTextLabel}>
                Title:
            </div>
            <div>
                <TS_Input<string>
                    className={inputTextField}
                    id={"user-title"}
                    value={this.state.user.title}
                    placeholder={"Title"}
                    onChange={(value: string) => this.setState(state => ({
                        user: {
                            ...state.user,
                            title: value
                        }
                    }))}
                    type={"text"}
                    name={generateHex(8)}
                    spellCheck={true}/>
            </div>
        </div>;
    };

    private renderFirstNameField = () => {
        return <div className={`${inputText} ll_v_l`}>
            <div className={inputTextLabel}>
                First name:
            </div>
            <div>
                <TS_Input<string>
                    className={inputTextField}
                    id={"user-first-name"}
                    value={this.state.user.first}
                    placeholder={"First name"}
                    onChange={(value: string) => this.setState(state => ({
                        user: {
                            ...state.user,
                            first: value
                        }
                    }))}
                    type={"text"}
                    name={generateHex(8)}
                    spellCheck={true}/>
            </div>
        </div>;
    };

    private renderLastNameField = () => {
        return <div className={`${inputText} ll_v_l`}>
            <div className={inputTextLabel}>
                Last name:
            </div>
            <div>
                <TS_Input<string>
                    className={inputTextField}
                    id={"user-last-name"}
                    value={this.state.user.last}
                    placeholder={"Last name"}
                    onChange={(value: string) => this.setState(state => ({
                        user: {
                            ...state.user,
                            last: value
                        }
                    }))}
                    type={"text"}
                    name={generateHex(8)}
                    spellCheck={true}/>
            </div>
        </div>;
    };

    private renderMailField = () => {
        return <div className={`${inputText} ll_v_l`}>
            <div className={inputTextLabel}>
                E-Mail:
            </div>
            <div>
                <TS_Input<string>
                    className={inputTextField}
                    id={"user-mail"}
                    value={this.state.user.mail}
                    placeholder={"E-Mail"}
                    onChange={(value: string) => this.setState(state => ({
                        user: {
                            ...state.user,
                            mail: value
                        }
                    }))}
                    type={"text"}
                    name={generateHex(8)}
                    spellCheck={true}/>
            </div>
        </div>;
    };
}
